import React, { type ReactNode, useState } from 'react';

import classnames from 'classnames';
import { Check, DollarSign, Minus, Sparkles, Tag } from 'lucide-react';

import Pill, { DefaultPillStyles } from 'common/common/Pill';
import TextToggle from 'common/common/TextToggle';
import Tooltip from 'common/common/Tooltip';
import Button from 'common/inputs/Button';
import LandingLogos from 'common/LandingLogos';
import Link from 'common/Link';
import nbspLastSpace from 'common/util/nbspLastSpace';

import 'css/components/www/_PricingV3.scss';

const Plans = [
  {
    name: 'Free',
    description: 'Set up your feedback portal with just the basics',
    features: [
      'Unlimited users',
      'Private Boards',
      'Changelog',
      'One integration',
      'Single sign-on',
    ],
    price: {
      year: 0,
      month: 0,
    },
    cta: {
      action: 'Get Started',
      buttonType: 'greenButton',
      link: '/register',
    },
    whatYouGet: 'What you get:',
    popular: false,
  },
  {
    name: 'Starter',
    description: 'Get more out of your feedback with advanced tools',
    features: [
      'Unlimited posts',
      'Custom domain',
      'Prioritization roadmap',
      'Two integrations',
      'Content translations',
    ],
    price: {
      year: 79,
      month: 99,
    },
    cta: {
      action: 'Start Trial',
      buttonType: 'greenButton',
      link: '/register?trial=starter',
    },
    whatYouGet: 'All Free features, plus:',
    popular: false,
  },
  {
    name: 'Growth',
    description: 'Scale insights across your team with integrations and automations',
    features: [
      'Unlimited integrations',
      'Custom board access',
      'Automate with AI',
      'User segmentation',
      'Admin roles',
    ],
    price: {
      year: 359,
      month: 399,
    },
    cta: {
      action: 'Start Trial',
      buttonType: 'greenButton',
      link: '/register?trial=growth',
    },
    whatYouGet: 'All Starter features, plus:',
    popular: true,
  },
  {
    name: 'Business',
    description: 'Deploy additional permissions, compliance, and customizations',
    features: [
      'Authentication integrations',
      'Salesforce integration',
      'HubSpot integration',
      'White label',
      'Security review',
    ],
    price: {
      custom: true,
    },
    cta: {
      action: 'Request Demo',
      buttonType: 'ghostButton',
      link: '/request-demo',
    },
    whatYouGet: 'Optional add-ons:',
    popular: false,
  },
];

interface Map {
  [key: string]: {
    label: string | ReactNode;
    tooltip?: string;
  };
}

const LabelTooltipMap: Map = {
  admins: { label: 'Admins', tooltip: 'Canny admins that manage feedback and roadmaps' },
  contributors: {
    label: 'Contributors',
    tooltip: 'Canny admins that contribute feedback',
  },
  users: { label: 'End users', tooltip: 'Anybody who posts, votes, or comments on feedback' },
  activePosts: {
    label: 'Active posts',
    tooltip: 'Posts that are not completed or closed',
  },
  boards: { label: 'Boards' },
  roadmaps: { label: 'Roadmaps' },
  autopilotCredits: {
    label: (
      <>
        Autopilot credits
        <Pill className="featurePill" pillStyle={DefaultPillStyles.Canny}>
          New
        </Pill>
      </>
    ),
  },
  core: { label: 'Core features' },
  api: { label: 'API & webhooks' },
  internalComments: { label: 'Internal comments', tooltip: 'Admin-only comments on posts' },
  customAccess: {
    label: 'Custom Board Access',
    tooltip: 'Limit board access to specific users',
  },
  customDomain: { label: 'Custom domain', tooltip: 'Use a custom domain like ideas.company.com' },
  privateAuthors: {
    label: 'Anonymized boards',
    tooltip: 'Make users anonymous to one another',
  },
  privateBoards: {
    label: 'Private boards',
    tooltip: 'Collect and share feedback privately with your team',
  },
  adminRoles: {
    label: 'Admin roles',
    tooltip: 'Customize admin permissions',
  },
  userSegmentation: {
    label: 'User segmentation',
    tooltip: 'Filter feedback to user groups you care about e.g. paying customers',
  },
  privateComments: { label: 'Private comments', tooltip: 'Make comments from end-users private' },
  customStatuses: { label: 'Custom statuses', tooltip: 'Add, remove, and update statuses' },
  disableCreation: {
    label: 'Disable new post creation',
    tooltip: 'Turn off new post creation from end users',
  },
  autoResponse: {
    label: 'Smart replies',
    tooltip: 'Automated replies on new posts to collect more details',
  },
  prioritySupport: { label: 'Priority support', tooltip: 'Jump the live chat queue' },
  translateContent: {
    label: 'Content translations',
    tooltip: 'Automatically translate user-generated content',
  },
  removeBranding: { label: 'Remove branding', tooltip: 'Remove "Powered by\u00a0Canny"' },
  summarizeComments: {
    label: 'Summarize comments',
    tooltip: 'Summarize user comments using AI',
  },
  emailWhitelabeling: {
    label: 'Email white labeling',
    tooltip: 'Send Canny emails from your domain',
  },
  fileAttachments: {
    label: 'Non-image file attachments',
    tooltip: 'Attach files to posts and comments. Images are free.',
  },
  intercom: { label: 'Intercom', tooltip: 'Capture feedback from people in Intercom' },
  github: { label: 'GitHub', tooltip: 'Sync Canny post with GitHub issues' },
  slack: { label: 'Slack', tooltip: 'Capture feedback and get notifications in Slack' },
  discord: { label: 'Discord', tooltip: 'Get instant notifications in Discord' },
  microsoftTeams: {
    label: 'Microsoft Teams',
    tooltip: 'Get instant notifications in Microsoft Teams',
  },
  zendesk: { label: 'Zendesk', tooltip: 'Capture feedback from Zendesk support tickets' },
  jira: { label: 'Jira', tooltip: 'Sync Canny posts with Jira issues' },
  asana: { label: 'Asana', tooltip: 'Sync Canny posts with Asana tasks' },
  linear: { label: 'Linear', tooltip: 'Sync Canny posts with Linear issues' },
  clickup: { label: 'ClickUp', tooltip: 'Sync Canny posts with ClickUp tasks' },
  gsuite: { label: 'G Suite', tooltip: 'Authenticate your teammates into Canny' },
  segment: { label: 'Segment', tooltip: 'Easily sync user data from Segment' },
  zapier: { label: 'Zapier', tooltip: 'Connect Canny to 750+ other apps' },
  chrome: { label: 'Chrome extension', tooltip: 'Track feedback from any webpage via Chrome' },
  azureAD: {
    label: 'Azure Active Directory',
    tooltip: 'Authenticate your teammates into Canny',
  },
  azureDevOps: {
    label: 'Azure DevOps',
    tooltip: 'Sync Canny posts with Azure DevOps work items',
  },
  okta: { label: 'Okta', tooltip: 'Allow your teammates to gain Canny admin access' },
  oneLogin: { label: 'OneLogin', tooltip: 'Allow your teammates to gain Canny admin access' },
  openID: {
    label: 'OpenID Connect (OIDC)',
    tooltip: 'Allow your teammates to gain Canny admin access',
  },
  hubspot: { label: 'HubSpot', tooltip: 'Capture feedback from contacts & sync deals' },
  salesforce: {
    label: 'Salesforce',
    tooltip: 'Capture feedback from Salesforce Leads, Contacts, Opportunities & Cases',
  },
  ccpa: { label: 'CCPA' },
  gdpr: { label: 'GDPR' },
  soc2: { label: 'SOC 2 report' },
  invoice: { label: 'Pay by invoice' },
  secReview: { label: 'Security review' },
};

const renderToggle = (
  timeframe: string,
  onChange: React.Dispatch<React.SetStateAction<string>>
) => {
  return (
    <div className="toggle">
      <TextToggle
        onChange={onChange}
        options={[
          {
            label: (
              <div className="yearlyDiscount">
                Yearly <Tag size={14} /> up to 20% off!
              </div>
            ),
            value: 'yearly',
          },
          {
            label: 'Monthly',
            value: 'monthly',
          },
        ]}
        selected={timeframe}
      />
    </div>
  );
};

const renderPlans = (selectedTimeframe: string) => {
  return (
    <div className="plans">
      {Plans.map((plan) => {
        let price;
        let timeframe;
        if (plan.price.custom) {
          price = "Let's Talk";
        } else {
          const amount = selectedTimeframe === 'yearly' ? plan.price.year : plan.price.month;
          price = '$' + amount;
          timeframe = '/month';
        }

        return (
          <div
            className={classnames({
              wrapper: true,
              popular: !!plan.popular,
            })}
            key={plan.name}>
            {plan.popular && (
              <div className="mostPopular">
                Most Popular <Sparkles size={16} />
              </div>
            )}
            <div className="plan">
              <div className="name">{plan.name}</div>
              <div className="description">{nbspLastSpace(plan.description)}</div>
              <div className="price">
                <div className="number">{price}</div>
                <div className="timeframe">{timeframe}</div>
              </div>
              <Link className="cta" to={plan.cta.link}>
                <Button buttonType={plan.cta.buttonType} value={plan.cta.action} />
              </Link>
              <div className="whatYouGet">{plan.whatYouGet}</div>
              <div className="features">
                {plan.features.map((feature) => {
                  return (
                    <div className="feature" key="feature">
                      <Check />
                      {feature}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const renderSectionHeader = (header: string, columns: string[] = ['', '', '', '']) => {
  return (
    <div className="row sectionHeader" key="sectionHeader">
      <div className="firstColumn">{header}</div>
      <div className="column">{columns[0]}</div>
      <div className="column">{columns[1]}</div>
      <div className="column">{columns[2]}</div>
      <div className="column">{columns[3]}</div>
    </div>
  );
};

const renderFirstColumn = (key: keyof typeof LabelTooltipMap) => {
  if (key === 'core') {
    return (
      <div className="firstColumn">
        <div className="labelAndTooltip">Core features</div>
        <div className="sublabel">
          Status updates, user profiles, single sign-on, admin reports, user reports,
          vote-on-behalf, tags, customization, admin badges, subdomain, widget, categories, ETAs,
          post&nbsp;owners
        </div>
        <Link className="learnMore" to="/features">
          See features page
        </Link>
      </div>
    );
  }

  const labelTooltip = LabelTooltipMap[key];
  return (
    <div className="firstColumn">
      <div className="labelAndTooltip">
        {labelTooltip.label}
        {labelTooltip?.tooltip && (
          <Tooltip value={nbspLastSpace(labelTooltip.tooltip)}>
            <div className="icon-help" />
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const renderCheckCell = (value: string) => {
  if (value === 'paid') {
    return (
      <div className="column">
        <DollarSign size={24} />
      </div>
    );
  }

  if (value === 'check') {
    return (
      <div className="column">
        <Check size={24} />
      </div>
    );
  }

  if (value === 'checkGray') {
    return (
      <div className="column gray">
        <Check size={24} />
      </div>
    );
  }

  return (
    <div className="column">
      <Minus size={24} />
    </div>
  );
};

const renderSliding = (
  timeframe: string,
  sliding: {
    custom?: boolean;
    monthly?: number;
    yearly?: number;
  }
) => {
  if (!sliding) {
    return;
  }

  const { custom, monthly, yearly } = sliding;
  if (custom) {
    return <span className="sliding">custom/mo/additional</span>;
  }

  if (timeframe === 'yearly') {
    return <span className="sliding">+${yearly}/mo/additional</span>;
  }

  if (timeframe === 'monthly') {
    return <span className="sliding">+${monthly}/mo/additional</span>;
  }

  return;
};

const renderPlanLimit = (timeframe: string, plan: string, limit: string) => {
  interface PlanLimitMap {
    [key: string]: {
      admins: {
        limit?: number;
        sliding?: {
          custom?: boolean;
          monthly?: number;
          yearly?: number;
        };
        contributors?: boolean;
        custom?: boolean;
      };
      [key: string]: any;
    };
  }
  const planLimits: PlanLimitMap = {
    free: {
      activePosts: 100,
      admins: { limit: 3 },
      contributors: 0,
      boards: 1,
      roadmaps: 0,
      autopilotCredits: 25,
    },
    starter: {
      admins: { limit: 3, sliding: { monthly: 20, yearly: 18 } },
      contributors: 0,
      boards: 3,
      roadmaps: 1,
      autopilotCredits: 100,
    },
    growth: {
      admins: { limit: 5, sliding: { monthly: 40, yearly: 36 } },
      autopilotCredits: 500,
    },
    biz: {
      admins: { limit: 5, sliding: { custom: true } },
      autopilotCredits: 'Custom',
    },
  };
  const value = planLimits[plan]?.[limit] ?? 'Unlimited';

  if (limit === 'admins') {
    const { limit: planLimit, sliding } = value;
    return (
      <div className="column adminLimit" key={plan}>
        {planLimit}
        {renderSliding(timeframe, sliding)}
      </div>
    );
  }

  return (
    <div className="column" key={plan}>
      {value}
    </div>
  );
};

const renderGridUserLimits = (selectedTimeframe: string) => {
  const limits = ['admins', 'contributors', 'users'];
  const plans = ['free', 'starter', 'growth', 'biz'];

  return limits.map((limit) => {
    return (
      <div className="row" key={limit}>
        {renderFirstColumn(limit)}
        {plans.map((plan) => {
          return renderPlanLimit(selectedTimeframe, plan, limit);
        })}
      </div>
    );
  });
};

const renderGridFeatureLimits = (selectedTimeframe: string) => {
  const limits = ['activePosts', 'boards', 'roadmaps', 'autopilotCredits'];
  const plans = ['free', 'starter', 'growth', 'biz'];

  return limits.map((limit) => {
    return (
      <div className="row" key={limit}>
        {renderFirstColumn(limit)}
        {plans.map((plan) => {
          return renderPlanLimit(selectedTimeframe, plan, limit);
        })}
      </div>
    );
  });
};

const renderGridFeatures = () => {
  const features = [
    'core',
    'privateBoards',
    'api',
    'customDomain',
    'internalComments',
    'translateContent',
    'fileAttachments',
    'prioritySupport',
    'customAccess',
    'privateAuthors',
    'adminRoles',
    'userSegmentation',
    'privateComments',
    'customStatuses',
    'disableCreation',
    'autoResponse',
    'summarizeComments',
    'removeBranding',
    'emailWhitelabeling',
  ];
  const plans = ['free', 'starter', 'growth', 'biz'];

  interface PlanFeatureMap {
    [key: string]: {
      [key: string]: string;
    };
  }
  const planFeatures: PlanFeatureMap = {
    free: {
      api: 'check',
      core: 'check',
      privateBoards: 'check',
    },
    starter: {
      core: 'check',
      api: 'check',
      customDomain: 'check',
      fileAttachments: 'check',
      internalComments: 'check',
      prioritySupport: 'check',
      privateBoards: 'check',
      translateContent: 'check',
    },
    growth: {
      core: 'check',
      api: 'check',
      customDomain: 'check',
      fileAttachments: 'check',
      internalComments: 'check',
      prioritySupport: 'check',
      translateContent: 'check',
      privateAuthors: 'check',
      privateBoards: 'check',
      customAccess: 'check',
      adminRoles: 'check',
      userSegmentation: 'check',
      privateComments: 'check',
      customStatuses: 'check',
      disableCreation: 'check',
      autoResponse: 'check',
      summarizeComments: 'check',
    },
    biz: {
      core: 'check',
      api: 'check',
      customDomain: 'check',
      fileAttachments: 'check',
      internalComments: 'check',
      prioritySupport: 'check',
      translateContent: 'check',
      privateAuthors: 'check',
      privateBoards: 'check',
      customAccess: 'check',
      adminRoles: 'check',
      userSegmentation: 'check',
      privateComments: 'check',
      customStatuses: 'check',
      disableCreation: 'check',
      autoResponse: 'check',
      summarizeComments: 'check',
      removeBranding: 'paid',
      emailWhitelabeling: 'paid',
    },
  };

  return features.map((feature) => {
    return (
      <div className="row" key={feature}>
        {renderFirstColumn(feature)}
        {plans.map((plan) => {
          return renderCheckCell(planFeatures[plan]?.[feature]);
        })}
      </div>
    );
  });
};

const renderGridIntegrations = () => {
  const integrations = [
    'intercom',
    'github',
    'slack',
    'discord',
    'microsoftTeams',
    'zendesk',
    'jira',
    'asana',
    'linear',
    'clickup',
    'gsuite',
    'segment',
    'zapier',
    'chrome',
    'azureAD',
    'azureDevOps',
    'okta',
    'oneLogin',
    'openID',
    'hubspot',
    'salesforce',
  ];
  const plans = ['free', 'starter', 'growth', 'biz'];

  interface PlanIntegrationMap {
    [key: string]: {
      [key: string]: string;
    };
  }
  const planIntegrations: PlanIntegrationMap = {
    free: {
      intercom: 'checkGray',
      github: 'checkGray',
      slack: 'checkGray',
      discord: 'checkGray',
      microsoftTeams: 'checkGray',
      zendesk: 'checkGray',
      jira: 'checkGray',
      asana: 'checkGray',
      linear: 'checkGray',
      clickup: 'checkGray',
      gsuite: 'checkGray',
    },
    starter: {
      intercom: 'checkGray',
      github: 'checkGray',
      slack: 'checkGray',
      discord: 'checkGray',
      microsoftTeams: 'checkGray',
      zendesk: 'checkGray',
      jira: 'checkGray',
      asana: 'checkGray',
      linear: 'checkGray',
      clickup: 'checkGray',
      gsuite: 'checkGray',
    },
    growth: {
      intercom: 'check',
      github: 'check',
      slack: 'check',
      discord: 'check',
      microsoftTeams: 'check',
      zendesk: 'check',
      jira: 'check',
      asana: 'check',
      linear: 'check',
      clickup: 'check',
      gsuite: 'check',
      segment: 'check',
      zapier: 'check',
      chrome: 'check',
    },
    biz: {
      intercom: 'check',
      github: 'check',
      slack: 'check',
      discord: 'check',
      microsoftTeams: 'check',
      zendesk: 'check',
      jira: 'check',
      asana: 'check',
      linear: 'check',
      clickup: 'check',
      gsuite: 'check',
      segment: 'check',
      zapier: 'check',
      chrome: 'check',
      azureAD: 'check',
      azureDevOps: 'check',
      okta: 'check',
      oneLogin: 'check',
      openID: 'check',
      hubspot: 'paid',
      salesforce: 'paid',
    },
  };

  return integrations.map((integration) => {
    return (
      <div className="row" key={integration}>
        {renderFirstColumn(integration)}
        {plans.map((plan) => {
          return renderCheckCell(planIntegrations[plan]?.[integration]);
        })}
      </div>
    );
  });
};

const renderGridCompliances = () => {
  const compliances = ['ccpa', 'gdpr', 'soc2', 'invoice', 'secReview'];
  const plans = ['free', 'starter', 'growth', 'biz'];

  interface PlanComplianceMap {
    [key: string]: {
      [key: string]: string;
    };
  }
  const planCompliances: PlanComplianceMap = {
    free: {
      ccpa: 'check',
      gdpr: 'check',
    },
    starter: {
      ccpa: 'check',
      gdpr: 'check',
    },
    growth: {
      ccpa: 'check',
      gdpr: 'check',
      soc2: 'check',
    },
    biz: {
      ccpa: 'check',
      gdpr: 'check',
      soc2: 'check',
      invoice: 'check',
      secReview: 'paid',
    },
  };

  return compliances.map((compliance) => {
    return (
      <div className="row" key={compliance}>
        {renderFirstColumn(compliance)}
        {plans.map((plan) => {
          return renderCheckCell(planCompliances[plan]?.[compliance]);
        })}
      </div>
    );
  });
};

const renderGrid = (selectedTimeframe: string) => {
  return (
    <div className="grid">
      <div className="header">
        <div className="firstColumn"></div>
        {Plans.map((plan) => {
          let price;
          let timeframe;
          if (plan.price.custom) {
            price = 'Custom';
          } else {
            const amount = selectedTimeframe === 'yearly' ? plan.price.year : plan.price.month;
            price = '$' + amount;
            timeframe = '/mo';
          }
          return (
            <div className="column plan" key={plan.name}>
              <div className="planName">{plan.name}</div>
              <div className="planPrice">
                <div className="number">{price}</div>
                <div className="timeframe">{timeframe}</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="table">
        {renderSectionHeader('User Types')}
        {renderGridUserLimits(selectedTimeframe)}
        {renderSectionHeader('Features')}
        {renderGridFeatureLimits(selectedTimeframe)}
        {renderGridFeatures()}
        {renderSectionHeader('Integrations', ['1', '2', 'Unlimited', 'Unlimited'])}
        {renderGridIntegrations()}
        {renderSectionHeader('Compliance')}
        {renderGridCompliances()}
      </div>
    </div>
  );
};

const PricingV3 = () => {
  const [timeframe, setTimeframe] = useState('yearly');

  return (
    <div className="pricingV3">
      {renderToggle(timeframe, setTimeframe)}
      {renderPlans(timeframe)}
      <LandingLogos />
      {renderGrid(timeframe)}
    </div>
  );
};

export default PricingV3;
